import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { verifyEmailToken, resendVerificationEmail } from "../api"; // assume resendVerificationEmail exists
import "./VerificationPage.css";

const VerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); 
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(""); 
  const [resendMessage, setResendMessage] = useState("");
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef(null);

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setStatus("error");
      setMessage("No verification token provided.");
      return;
    }

    const verifyToken = async () => {
      try {
        const result = await verifyEmailToken(token);
        if (result.success) {
          setStatus("success");
          setMessage(result.message || "Email verified successfully!");

          if (result.token) {
            localStorage.setItem("token", result.token);
            localStorage.setItem("isLoggedIn", "true");
            if (result.email) localStorage.setItem("userEmail", result.email);
            if (result.data) {
              localStorage.setItem("userData", JSON.stringify(result.data));
              if (result.data.name) localStorage.setItem("userName", result.data.name);
            }

            const timer = setTimeout(() => {
              navigate("/", { replace: true });
            }, 3000);
            return () => clearTimeout(timer);
          }
        } else {
          if (result.email) setEmail(result.email);
          setStatus("error");
          setMessage(result.message || "Verification failed.");
        }
      } catch (error) {
        console.error("Verification error:", error);
        if (error.response?.data?.email) setEmail(error.response.data.email);
        setStatus("error");
        setMessage("An error occurred during verification.");
      }
    };
    verifyToken();
  }, []); 

  const handleResend = async () => {
    if (!email) {
      setResendMessage("Email address not available. Please try again from login.");
      return;
    }
    setResendMessage("");
    setStatus("resending");
    try {
      const result = await resendVerificationEmail(email);
      if (result.success) {
        setResendMessage("Verification email resent! Please check your inbox.");
        setStatus("error"); 
      } else {
        setResendMessage(result.message || "Failed to resend email.");
        setStatus("error");
      }
    } catch (error) {
      console.error("Resend error:", error);
      setResendMessage("An error occurred. Please try again later.");
      setStatus("error");
    }
  };

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  return (
    <>
      <div ref={headerRef}>
        <Header forceSolid />
      </div>
      <div className="verification-page" style={{ marginTop: `${headerHeight}px` }}>
        <div className="verification-container">
          {status === "loading" && (
            <>
              <div className="spinner large"></div>
              <p>Verifying your email...</p>
            </>
          )}
          {status === "success" && (
            <>
              <div className="success-icon">✓</div>
              <h2>Email Verified!</h2>
              <p>{message}</p>
              <p className="redirect-note">You will be redirected shortly...</p>
              <button onClick={() => navigate("/")} className="verification-btn">
                Go to Home
              </button>
            </>
          )}
          {status === "error" && (
            <>
              <div className="error-icon">✗</div>
              <h2>Verification Failed</h2>
              <p>{message}</p>
              {email && (
                <>
                  <p className="resend-prompt">Your verification link may have expired.</p>
                  <button
                    onClick={handleResend}
                    className="verification-btn resend-btn"
                    disabled={status === "resending"}
                  >
                    {status === "resending" ? "Sending..." : "Resend Verification Email"}
                  </button>
                  {resendMessage && <p className="resend-message">{resendMessage}</p>}
                </>
              )}
              <button onClick={() => navigate("/login")} className="verification-btn secondary">
                Back to Login
              </button>
            </>
          )}
          {status === "resending" && (
            <>
              <div className="spinner large"></div>
              <p>Sending verification email...</p>
            </>
          )}
        </div>
      </div>
      {/* <Footer /> */}
    </>
  );
};

export default VerificationPage;